import { useEffect, useState } from "react";
import {
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import BoxAlert from "../../components/boxAlert/index.jsx";
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import LogsList from "./components/logsList/index.jsx";
import { getAdminActionsLog } from "./services/getAdminActionsLog.js";
import styles from "./css/logs.module.css";

const ACTION_OPTIONS = [
    { value: "ALL", label: "Todas as ações" },
    { value: "CREATE", label: "Criação" },
    { value: "UPDATE", label: "Atualização" },
    { value: "DELETE", label: "Exclusão" },
    { value: "READ", label: "Consulta" },
    { value: "SUPPORT_START", label: "Acesso de suporte iniciado" },
    { value: "SUPPORT_END", label: "Acesso de suporte encerrado" },
];

const LIMIT_OPTIONS = [20, 50, 100];

const EMPTY_FILTERS = {
    action: "ALL",
    adminName: "",
    companyName: "",
    startDate: "",
    endDate: "",
};

export default function Logs() {
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [meta, setMeta] = useState(null);
    const [limit, setLimit] = useState(20);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [warnAlert, setWarnAlert] = useState({ show: false, title: null, text: null });

    useEffect(() => {
        fetchLogs(1);
    }, [limit]);

    const fetchLogs = async (page, filtersOverride) => {
        setLoading(true);
        const currentFilters = filtersOverride ?? filters;

        try {
            const response = await getAdminActionsLog({
                page,
                limit,
                action: currentFilters.action,
                adminName: currentFilters.adminName,
                companyName: currentFilters.companyName,
                startDate: currentFilters.startDate,
                endDate: currentFilters.endDate,
            });

            setLogs(response?.data || []);
            setMeta(response?.meta || null);

        } catch (error) {
            console.error('ERROR - GET ADMIN ACTIONS LOG:', error);
            setWarnAlert({ show: true, title: 'Erro', text: error?.response?.data?.error || 'Não foi possível buscar os logs administrativos.' });
            setLogs([]);
            setMeta(null);

        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        fetchLogs(1);
    };

    const handleClearFilters = () => {
        setFilters(EMPTY_FILTERS);
        fetchLogs(1, EMPTY_FILTERS);
    };

    const handlePageChange = (_event, page) => {
        fetchLogs(page);
    };

    return (
        <div>
            <Header>
                <h2 style={{ color: '#fff' }}>Logs admin</h2>
            </Header>

            <div className={styles.container}>
                <div className={styles.filtersCard}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ rowGap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Ação</InputLabel>
                            <Select
                                label="Ação"
                                value={filters.action}
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                            >
                                {ACTION_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            label="Administrador"
                            placeholder="Nome do(a) administrador(a)"
                            value={filters.adminName}
                            onChange={(e) => handleFilterChange('adminName', e.target.value)}
                            sx={{ minWidth: 220 }}
                        />

                        <TextField
                            size="small"
                            label="Empresa"
                            placeholder="Nome da empresa"
                            value={filters.companyName}
                            onChange={(e) => handleFilterChange('companyName', e.target.value)}
                            sx={{ minWidth: 220 }}
                        />

                        <TextField
                            size="small"
                            label="De"
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />

                        <TextField
                            size="small"
                            label="Até"
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />

                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                sx={{ boxShadow: "none", textTransform: "none", backgroundColor: 'var(--color-primary-background)' }}
                            >
                                Buscar
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: "none" }}
                            >
                                Limpar
                            </Button>
                        </Stack>
                    </Stack>
                </div>

                {loading ? (
                    <Loading />

                ) : logs.length > 0 ? (
                    <>
                        

                        <Stack direction="row" spacing={2} flexWrap="no-wrap" useFlexGap sx={{ rowGap: 2, justifyContent: 'space-between', marginBottom: 1, marginTop: 4 }}>
                            <h1>Lista de logs</h1>
                            
                            <FormControl size="small" sx={{ minWidth: 130, backgroundColor: "#fff", borderRadius: 1 }}>
                                <Select value={limit} onChange={(e) => setLimit(e.target.value)}>
                                    {LIMIT_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>{option} por página</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {meta && (
                            <div className={styles.resultsInfo}>
                                <Typography variant="body2" color="text.secondary">
                                    {meta.totalItems} {meta.totalItems !== 1 ? 'logs encontrados' : 'log encontrado'}
                                </Typography>
                            </div>
                        )}

                        <LogsList logs={logs} />

                        {meta && meta.totalPages > 1 && (
                            <div className={styles.paginationArea}>
                                <Pagination
                                    count={meta.totalPages}
                                    page={meta.currentPage}
                                    onChange={handlePageChange}
                                    shape="rounded"
                                    sx={{
                                        '& .Mui-selected': {
                                            backgroundColor: 'var(--color-primary-background) !important',
                                            color: '#fff',
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </>

                ) : (
                    <p className={styles.noDataText}>
                        Não há nenhum log administrativo a ser visto por aqui.
                    </p>
                )}
            </div>

            <BoxAlert
                show={warnAlert.show}
                title={warnAlert.title}
                text={warnAlert.text}
                onClose={() => setWarnAlert({ show: false, title: null, text: null })}
            />
        </div>
    );
}
